import type {AxiosError} from 'axios';
import axios from 'axios';
import * as types from '../mutation-types';
import {Form} from '@/plugins/FWForms/index';
import type {RootState} from '@/types/RootState';
import type {Module} from 'vuex';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {Timestamps, Uuid} from '@/types/common.ts';
import {push} from 'notivue'

export interface AffiliateInterface extends Timestamps, Uuid {
    active: boolean,
    agree_tos_at: string | null,
    amount: number,
    duration_months?: number | null,
    logo: string | null,
    name: string,
    partner_email: string,
    partner_name: string,
    partner_website: string,
    referral: any[],
    slug: string,
    trial_days: number,
    type: string,
    url: string | null,
}

export interface AffiliateState {
    loading: Record<string, boolean>;
    affiliate: AffiliateInterface | null;
    affiliatePayouts: any[];
    affiliateApplication: any;
    affiliatePayoutsFuture: number;
    affiliatePayoutsPast: number;
    affiliateForm: Form<any>;
}


const state: AffiliateState = {
    loading: {
        affiliateApplication: false,
        affiliateAgreeTOS: false,
        getAffiliate: false,
        getAffiliatePayouts: false,
    },
    affiliate: null,
    affiliatePayouts: [],
    affiliateApplication: {},
    affiliatePayoutsFuture: 0,
    affiliatePayoutsPast: 0,
    affiliateForm: new Form(),
};


const mutations = {
    [types.SET_AFFILIATE_LOADING](state: AffiliateState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_AFFILIATE](state: AffiliateState, affiliate: AffiliateInterface) {
        state.affiliate = affiliate;
    },
    [types.SET_AFFILIATE_PAYOUTS](state: AffiliateState, payouts) {
        state.affiliatePayouts = payouts;
    },
    [types.SET_AFFILIATE_FUTURE_PAYOUT](state: AffiliateState, payout) {
        state.affiliatePayoutsFuture = payout;
    },
    [types.SET_AFFILIATE_PAST_PAYOUT](state: AffiliateState, payout) {
        state.affiliatePayoutsPast = payout;
    },
    [types.SET_AFFILIATE_APPLICATION](state: AffiliateState, application) {
        state.affiliateApplication = application;
    },
};


const actions = {

    /**
     * Submit Affiliate application
     */
    async submitAffiliateApplication({commit, state}, data) {
        try {
            const responseData = await performApiRequest(commit, 'SET_AFFILIATE_LOADING', 'submit', () => axios.post('/v1/affiliate/signup', data));
            commit(types.SET_AFFILIATE_APPLICATION, responseData.application);
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as Record<string, string[]>)?.errors || [];
            state.affiliateForm.errors.set(errors);
            throw error;
        }
    },

    /**
     * Get Affiliate application
     */
    async affiliateApplications({commit, state}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_AFFILIATE_LOADING', 'affiliateApplication', () => axios.get('/v1/affiliate/application'));
            commit(types.SET_AFFILIATE_APPLICATION, responseData.application);
            commit(types.SET_AFFILIATE, responseData.affiliate);
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as Record<string, string[]>)?.errors || [];
            state.affiliateForm.errors.set(errors);
        }
    },

    /**
     * Get Affiliate
     */
    async getAffiliate({commit}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_AFFILIATE_LOADING', 'getAffiliate', () => axios.get('/v1/affiliate'));
            commit(types.SET_AFFILIATE, responseData.affiliate);
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as { errors: Record<string, string[]> })?.errors || {};
            state.affiliateForm.errors.set(errors);
        }
    },

    /**
     * Get Affiliates Payouts
     */
    async getAffiliatePayouts({commit}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_AFFILIATE_LOADING', 'getAffiliatePayouts', () => axios.get('/v1/affiliate/payouts'));
            commit(types.SET_AFFILIATE_PAYOUTS, responseData.payouts);
            commit(types.SET_AFFILIATE_FUTURE_PAYOUT, responseData.future_payouts);
            commit(types.SET_AFFILIATE_PAST_PAYOUT, responseData.past_payouts);
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as { errors: Record<string, string[]> })?.errors || {};
            state.affiliateForm.errors.set(errors);
        }
    },

    /**
     * Affiliate TOS agreement
     */
    async affiliateTOSAgree({commit}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_AFFILIATE_LOADING', 'getAffiliate', () => axios.post('/v1/affiliate/agree-tos'));
            push.success({
                message: 'Agree to TOS recorded successfully!',
            });
            return responseData;
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as { errors: Record<string, string[]> })?.errors || {};
            state.affiliateForm.errors.set(errors);
        }
    },
};


const affiliateModule: Module<AffiliateState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters: {
        loading: (state: AffiliateState) => state.loading,
        affiliate: (state: AffiliateState) => state.affiliate,
        affiliateForm: (state: AffiliateState) => state.affiliateForm,
        affiliatePayouts: (state: AffiliateState) => state.affiliatePayouts,
        affiliateApplication: (state: AffiliateState) => state.affiliateApplication,
        affiliatePayoutsPast: (state: AffiliateState) => state.affiliatePayoutsPast,
        affiliatePayoutsFuture: (state: AffiliateState) => state.affiliatePayoutsFuture,
    }
};


export default affiliateModule;